import { Eyebrow } from '@/components/ui';
import { ChevronDownIcon } from '@/lib/icons';

const QUESTIONS = [
  {
    q: 'Do I have to upload a programme first?',
    a: 'No. You can start an empty session and add exercises as you go, or pick one of the built-in templates (3-day full body, upper/lower, push-pull-legs). Importing is for people who already run a programme in a spreadsheet — CSV and JSON both work, and every movement is matched to the exercise library so history stays continuous.',
  },
  {
    q: 'Which units does it use?',
    a: 'Everything is stored canonically in kilograms and centimetres. Switch to pounds at any time and the whole interface converts for display only — 1 kg = 2.20462 lb, rounded to the nearest 0.5 lb so the numbers match the plates on the bar. Switching units never rewrites your history.',
  },
  {
    q: 'Does the watch app need my phone?',
    a: 'No. The watchOS companion logs sets on its own and holds them in a local queue. When your phone is reachable it hands them over immediately; when it is not, they transfer the moment it is. The watch never holds an access token and never talks to the cloud directly, and sets are deduplicated by idempotency key, so a set cannot be logged twice.',
  },
  {
    q: 'What happens if I lose signal mid-session?',
    a: 'Nothing. A gym basement with no signal is the normal case, not an edge case. The Grid and both apps write locally first and sync afterwards, oldest-first, in batches. Your session is never blocked on connectivity, and totals are recomputed server-side from the sets that arrive.',
  },
  {
    q: 'Can I get my data out?',
    a: 'Yes — one button, full history as CSV or JSON, including programmes, sessions, every set, every personal record and your AI coach’s reports. Deleting your account hard-deletes your rows within 30 days. Row-level security means your rows were only ever visible to you in the first place.',
  },
  {
    q: 'Do I need Premium to get a programme?',
    a: 'No. Generate your plan on the free trial, then keep logging it on the free tier for as long as you like — the tracker stays free. Premium is for the AI coach that keeps reading your log and rewriting what comes next, week by week.',
  },
  {
    q: 'Is the AI coach giving me medical advice?',
    a: 'No. It is a training log analyser with a reasoning layer: it detects progressive overload, fatigue and dropped compliance, then prescribes adjustments inside hard safety rails (never more than +10% load, never more than ±30% volume, never above RPE 10). It will not diagnose anything, and it deliberately blocks movements you have flagged as injured. For pain, see a clinician.',
  },
  {
    q: 'Can I cancel Premium?',
    a: 'Two taps, any time. Your log stays intact and stays free — you keep every session, every PR and full export access. If you come back later, the AI coach picks up from your history rather than starting over.',
  },
];

export function Faq() {
  return (
    <section id="faq" className="border-b border-hairline bg-page-subtle py-20">
      <div className="mx-auto max-w-[900px] px-4 sm:px-6">
        <div className="mb-10 flex flex-col gap-4">
          <Eyebrow>Straight answers</Eyebrow>
          <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
            Questions lifters actually ask.
          </h2>
        </div>

        <div className="divide-y divide-hairline border-y border-hairline">
          {QUESTIONS.map((item, index) => (
            <details key={item.q} className="group py-5" open={index === 0}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left">
                <span className="text-[15px] font-bold text-ink">{item.q}</span>
                <ChevronDownIcon className="shrink-0 text-base text-ink-muted transition-transform duration-200 group-open:rotate-180" />
              </summary>
              <p className="mt-3 max-w-[74ch] text-[14px] leading-relaxed text-ink-muted">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
